import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../shadcn/components/ui/Dialog';
import { Button } from '../../../shadcn/components/ui/Button';
import { Input } from '../../../shadcn/components/ui/Input';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '../../../shadcn/components/ui/Select';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../../shadcn/components/ui/Table';
import { Eye, Trash, LogIn } from 'lucide-react'; // Import LogIn icon
import { IndividualInformationModal } from './IndividualModal';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom'; // Import useNavigate for navigation

const RegisteredUsers = () => {
  const [open, setOpen] = useState(false);
  const [userData, setUserData] = useState({
    firstName: '',
    lastName: '',
    mobileNumber: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    email: '',
    education: '',
    countryOfBirth: '',
    birthDate: '',
    password: '',
    role: '',
  });
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editUserId, setEditUserId] = useState(null);
  const token = localStorage.getItem('authToken');
  const navigate = useNavigate(); // Initialize useNavigate

  // Fetch all users on component mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.post(
          'https://silver-tlc-backend-delta.vercel.app/api/v1/superadmin/register-newuser-get',
          { token }
        );
        setUsers(response.data.userData); // Map user data correctly
      } catch (error) {
        console.error('Error fetching users:', error);
        toast.error('Failed to fetch users');
      }
    };
    fetchUsers();
  }, [token]);

  // Handle login as user
  const handleLoginAsUser = async (email) => {
    const token = localStorage.getItem('authToken');
    try {
      const response = await axios.post(
        'https://silver-tlc-backend-delta.vercel.app/api/v1/superadmin/login-anyuser',
        {
          email,
          token, // Token is required for this endpoint
        }
      );

      console.log(response.data.data.name);
      if (response.data.status) {
        // Clear Super Admin info from localStorage
        localStorage.removeItem('authToken');
        localStorage.removeItem('userRole');
        localStorage.removeItem('email');
        localStorage.removeItem('userId');
        localStorage.removeItem('name');

        // Set the new user's info in localStorage
        localStorage.setItem('authToken', response.data.data.token);
        localStorage.setItem('userRole', response.data.data.role);
        localStorage.setItem('email', response.data.data.email);
        localStorage.setItem('userId', response.data.data.userid);
        localStorage.setItem('name', response.data.data.name);

        // Redirect to the respective dashboard based on the user's role
        const role = response.data.role; // Assuming the role is returned in the response
        if (role === 'Service Provider') {
          navigate('/servicedashboard');
        } else if (role === 'Managed Care Organizations') {
          navigate('/Managed Care Organization\'s');
        } else if (role === 'Individual') {
          navigate('/Dashboard');
        } else if (role === 'Property Owner') {
          navigate('/propertyownerdashboard');
        } else if (role === 'Non Profits') {
          navigate('/501 C3 Corporations');
        } else if (role === 'Real Estate Professionals') {
          navigate('/Hospital/HospitalSystems');
        } else if (role === 'SuperAdmin') {
          navigate('/superadmin');
        } else {
          navigate('/'); // Fallback to home if role is not recognized
        }
      } else {
        toast.error('Failed to login as user');
      }
    } catch (error) {
      console.error('Error logging in as user:', error);
      toast.error('Failed to login as user');
    }
  };

  // Handle form submission for creating or updating a user
  const handleSubmit = async () => {
    try {
      if (isEditMode) {
        // Update existing user
        const response = await axios.put(
          'https://silver-tlc-backend-delta.vercel.app/api/v1/superadmin/register-newuser-update',
          {
            ...userData,
            token,
            userId: editUserId,
          }
        );
        // Ensure the response data is correctly structured
        const updatedUser = {
          ...response.data,
          personalDetails: {
            firstName: userData.firstName,
            lastName: userData.lastName,
            mobileNumber: userData.mobileNumber,
            address: userData.address,
            city: userData.city,
            state: userData.state,
            zip: userData.zip,
            email: userData.email,
            education: userData.education,
            countryOfBirth: userData.countryOfBirth,
            birthDate: userData.birthDate,
            password: userData.password,
          },
          role: userData.role,
        };
        setUsers(users.map(user => user._id === editUserId ? updatedUser : user));
        toast.success('User updated successfully');
      } else {
        // Create new user
        const response = await axios.post(
          'https://silver-tlc-backend-delta.vercel.app/api/v1/superadmin/register-newuser-create',
          {
            ...userData,
            token,
          }
        );
        // Ensure the response data is correctly structured
        const newUser = {
          ...response.data,
          personalDetails: {
            firstName: userData.firstName,
            lastName: userData.lastName,
            mobileNumber: userData.mobileNumber,
            address: userData.address,
            city: userData.city,
            state: userData.state,
            zip: userData.zip,
            email: userData.email,
            education: userData.education,
            countryOfBirth: userData.countryOfBirth,
            birthDate: userData.birthDate,
            password: userData.password,
          },
          role: userData.role,
        };
        setUsers([...users, newUser]);
        toast.success('User registered successfully');
      }
      resetForm();
      setOpen(false);
    } catch (error) {
      console.error('Error creating/updating user:', error);
      toast.error(isEditMode ? 'Update failed' : 'Registration failed');
    }
  };

  // Reset form state
  const resetForm = () => {
    setUserData({
      firstName: '',
      lastName: '',
      mobileNumber: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      email: '',
      education: '',
      countryOfBirth: '',
      birthDate: '',
      password: '',
      role: '',
    });
    setIsEditMode(false);
    setEditUserId(null);
  };

  // Handle viewing individual user details
  const handleViewClick = (user) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  // Handle editing user details
  const handleEditClick = (user) => {
    setUserData({
      firstName: user.personalDetails?.firstName || '',
      lastName: user.personalDetails?.lastName || '',
      mobileNumber: user.personalDetails?.mobileNumber || '',
      address: user.personalDetails?.address || '',
      city: user.personalDetails?.city || '',
      state: user.personalDetails?.state || '',
      zip: user.personalDetails?.zip || '',
      email: user.personalDetails?.email || '',
      education: user.personalDetails?.education || '',
      countryOfBirth: user.personalDetails?.countryOfBirth || '',
      birthDate: user.personalDetails?.birthDate || '',
      password: user.personalDetails?.password || '',
      role: user.role || '',
    });
    setEditUserId(user.userId);
    setIsEditMode(true);
    setOpen(true);
  };

  // Handle deleting a user
  const handleDeleteClick = async (userId) => {
    const token = localStorage.getItem("authToken");
    console.log(token);
  
    try {
      await axios.delete(
        'https://silver-tlc-backend-delta.vercel.app/api/v1/superadmin/register-newuser-delete',
        {
          data: { token, userId }, // Use `data` for the request body
        }
      );
      setUsers(users.filter(user => user._id !== userId)); // Use `_id` instead of `userId`
      toast.success('User deleted successfully');
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Deletion failed');
    }
  };

  return (
    <div className="space-y-8 w-[75vw] md:w-[90vw] lg:px-9 lg:w-full mt-5">
      <div className="container mx-auto p-2">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Registered Users</h2>
          <Dialog
            open={open}
            onOpenChange={(isOpen) => {
              if (!isOpen) {
                resetForm(); // Reset form when dialog is closed
              }
              setOpen(isOpen);
            }}
          >
            <DialogTrigger asChild>
              <Button className="bg-purple-600 hover:bg-purple-700">
                Register New User
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px] max-w-[400px] max-h-[800px] overflow-y-scroll lg:overflow-y-hidden rounded-md bg-white">
              <DialogHeader>
                <DialogTitle className="flex gap-2 items-center justify-center">
                  {isEditMode ? 'Edit User' : 'Register New User'}
                </DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
                {/* Form fields */}
                {Object.keys(userData).map((key) => (
                  <div className="space-y-2" key={key}>
                    <label className="text-sm capitalize">{key}</label>
                    {key === 'state' || key === 'countryOfBirth' || key === 'role' ? (
                      <Select
                        value={userData[key]}
                        onValueChange={(value) =>
                          setUserData({ ...userData, [key]: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={`Select ${key}`} />
                        </SelectTrigger>
                        <SelectContent>
                          {key === 'state' ? (
                            <>
                              <SelectItem value="ny">New York</SelectItem>
                              <SelectItem value="ca">California</SelectItem>
                            </>
                          ) : key === 'countryOfBirth' ? (
                            <>
                              <SelectItem value="us">United States</SelectItem>
                              <SelectItem value="uk">United Kingdom</SelectItem>
                            </>
                          ) : (
                            <>
                              <SelectItem value="Individual">Individual</SelectItem>
                              <SelectItem value="Service Provider">Service Provider</SelectItem>
                              <SelectItem value="Managed Care Organizations">Managed Care Organizations</SelectItem>
                              <SelectItem value="Property Owner">Property Owner</SelectItem>
                              <SelectItem value="Real Estate Professionals">Real Estate Professionals</SelectItem>
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        placeholder={key}
                        value={userData[key]}
                        onChange={(e) =>
                          setUserData({ ...userData, [key]: e.target.value })
                        }
                        type={key === 'birthDate' ? 'date' : 'text'}
                      />
                    )}
                  </div>
                ))}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button
                  className="bg-purple-600 hover:bg-purple-700"
                  onClick={handleSubmit}
                >
                  {isEditMode ? 'Update' : 'Save'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
        <div className="overflow-x-auto  border pr-44 rounded-lg">
          <Table> 
            <TableHeader>
              <TableRow className="bg-gray-100">
                <TableHead className="w-[150px]">Name</TableHead>
                <TableHead>Address</TableHead>
                <TableHead>Phone Number</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>City</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user, index) => (
                <TableRow key={index}>
                  <TableCell>{`${user.personalDetails.firstName} ${user.personalDetails.lastName}`}</TableCell>
                  <TableCell>{user.personalDetails.address}</TableCell>
                  <TableCell>{user.personalDetails.mobileNumber}</TableCell>
                  <TableCell>{user.personalDetails.email}</TableCell>
                  <TableCell>{user.personalDetails.city}</TableCell>
                  <TableCell>{user.role}</TableCell>
                  <TableCell>
                    <div className="flex justify-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditClick(user)}
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M7.13388 16.8751L17.13388 6.8751L13.125 2.86621L3.125 12.8663V16.8751H7.13388ZM13.125 4.63398L15.36612 6.8751L13.75 8.49125L11.50888 6.25011L13.125 4.63398ZM10.625 7.13399L12.86612 9.3751L6.61612 15.6251H4.375V13.384L10.625 7.13399Z"
                            fill="#171A1F"
                          />
                        </svg>
                        <span className="">Edit</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewClick(user)}
                      >
                        <Eye className="h-4 w-4" />
                        <span className="">View</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteClick(user.userId)}
                      >
                        <Trash className="h-4 w-4" />
                        <span className="">Delete</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleLoginAsUser(user.personalDetails.email)}
                      >
                        <LogIn className="h-4 w-4" />
                        <span className="">Login</span>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {selectedUser && (
            <IndividualInformationModal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              userData={{
                ...selectedUser.personalDetails,
                role: selectedUser.role,
              }}
            />
          )}
        </div>
      </div>
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

export default RegisteredUsers;